/**
 * A "Klickprozess" page's process in the process plugin's model
 * (plugins/process-tool; its data model: the tool's ENTWICKLERDOKUMENTATION.md
 * §7), so the page import writes the block the plugin draws. The plugin's own
 * server hooks convert the same shape (plugins/process-tool/server.js).
 */

const LEGACY_TYPES = { task: 'aufgabe', gateway: 'entscheidung', start: 'start', end: 'ende' };

const text = (value) => (typeof value === 'string' ? value : '');
const finite = (value) => typeof value === 'number' && Number.isFinite(value);
const clamp01 = (value) => Math.min(1, Math.max(0, Math.round(value * 1000) / 1000));
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The old Klickprozess shape in the tool's model: lanes in order as the lanes
 * of one pool, a node's place as `lage` relative to the diagram's width and its
 * lane, its title without the "Aufgabe 02:" the tool shows as the number.
 */
export function legacyToProcess(old) {
  const nodes = Array.isArray(old?.nodes) ? old.nodes : [];
  const edges = Array.isArray(old?.edges) ? old.edges : [];

  if (!nodes.length) return null;

  const width = finite(old.width) && old.width > 0 ? old.width : 1;
  const lanes = (Array.isArray(old.lanes) ? old.lanes : [])
    .filter((lane) => lane && finite(lane.y) && finite(lane.height))
    .map((lane) => ({ name: text(lane.name), y: lane.y, height: lane.height }));

  const bahnen = lanes.length
    ? lanes.map((lane, index) => ({ id: `b${index + 1}`, name: lane.name, pool: 'p1' }))
    : [{ id: 'b1', name: '', pool: 'p1' }];

  const laneOf = (node) => {
    if (finite(node.lane) && lanes[node.lane - 1]) return node.lane - 1;
    const y = finite(node.y) ? node.y : 0;
    return Math.max(0, lanes.findIndex((lane) => y >= lane.y && y <= lane.y + lane.height));
  };

  const elemente = nodes
    .filter((node) => node && text(node.id))
    .map((node) => {
      const id = text(node.id);
      const index = laneOf(node);
      const lane = lanes[index];
      const prefix = new RegExp(`^\\s*(Aufgabe\\s+)?${escapeRegExp(id)}\\s*[:.-]\\s*`, 'i');

      return {
        nr: id,
        nummer: /^\d{1,3}$/.test(id) ? id : undefined,
        typ: LEGACY_TYPES[node.kind] ?? 'aufgabe',
        bahn: bahnen[index].id,
        titel: text(node.title).replace(prefix, '').trim(),
        ereignis: '',
        system: '',
        aufgabenart: '',
        erklaerung: text(node.text),
        schritte: Array.isArray(node.steps) ? node.steps.map(text).filter(Boolean) : [],
        hinweis: text(node.hint),
        ...(finite(node.x) && finite(node.y)
          ? {
            lage: {
              x: clamp01(node.x / width),
              y: lane ? clamp01((node.y - lane.y) / (lane.height || 1)) : 0.5,
            },
          }
          : {}),
      };
    });

  const ids = new Set(elemente.map((element) => element.nr));
  const numbered = elemente.filter((element) => element.nummer).length >= 3;

  return {
    titel: text(old.title).replace(/^Prozess:\s*/i, ''),
    beschriftung: true,
    nummern: numbered,
    nummern_original: numbered,
    pools: [{ id: 'p1', name: '' }],
    bahnen,
    elemente: elemente.map((element) => (numbered ? element : { ...element, nummer: undefined })),
    verbindungen: edges
      .filter((edge) => edge && ids.has(text(edge.from)) && ids.has(text(edge.to)))
      .map((edge) => ({ von: text(edge.from), nach: text(edge.to), bedingung: text(edge.label) })),
    teilnehmer: [],
    nachrichten: [],
  };
}

/** A process as the block an article stores. */
export function processBlock(process) {
  return `:::prozess\n${JSON.stringify(process, null, 1)}\n:::`;
}
